import type {
  ContextDockContent,
  ContextTool,
  ContextToolGroup,
} from '@affine/core/modules/streamlain/copilot/context-dock';
import {
  EDGELESS_DOCK_PRESENTATION_ENTRY,
  type EdgelessDock,
  type EdgelessDockEntryInfo,
  type EdgelessDockState,
} from '@blocksuite/affine/widgets/edgeless-toolbar';
import { Observable } from 'rxjs';

/** The canvas's part of the dock: its tools, without the capture. */
export type EdgelessToolsContent = Pick<
  ContextDockContent,
  'groups' | 'locked' | 'autoHide' | 'island'
>;

export const EMPTY_EDGELESS_TOOLS: EdgelessToolsContent = {
  groups: [],
  locked: false,
  autoHide: false,
  island: true,
};

/** The gap between quick tools in the row. */
const QUICK_TOOLS_GAP = 10;

function tool(
  dock: EdgelessDock,
  entry: Pick<EdgelessDockEntryInfo, 'id' | 'name' | 'active'> & {
    width: number | 'fill';
  }
): ContextTool {
  return {
    kind: 'mount',
    id: entry.id,
    label: entry.name,
    active: entry.active,
    width: entry.width,
    mount: container => dock.mount(entry.id, container),
  };
}

/** The canvas's dock state as context-dock groups: quick tools before the island, senior after it. */
export function edgelessDockContent(
  dock: EdgelessDock,
  state: EdgelessDockState,
  presentationLabel: string
): EdgelessToolsContent {
  const { locked, autoHide } = state;
  if (state.mode === 'none') return EMPTY_EDGELESS_TOOLS;
  if (state.mode === 'presenting') {
    // The presentation's controls take the whole row: the island steps aside meanwhile.
    return {
      locked,
      autoHide,
      island: false,
      groups: [
        {
          id: EDGELESS_DOCK_PRESENTATION_ENTRY,
          side: 'start',
          overflow: 'page',
          gap: 0,
          tools: [
            tool(dock, {
              id: EDGELESS_DOCK_PRESENTATION_ENTRY,
              name: presentationLabel,
              active: true,
              width: 'fill',
            }),
          ],
        },
      ],
    };
  }
  const quick = state.entries.filter(entry => entry.kind === 'quick');
  const senior = state.entries.filter(entry => entry.kind === 'senior');
  const groups: ContextToolGroup[] = [];
  if (quick.length) {
    groups.push({
      id: 'quick',
      side: 'start',
      overflow: 'menu',
      gap: QUICK_TOOLS_GAP,
      tools: quick.map(entry => tool(dock, entry)),
      openOverflow: (target, ids) =>
        dock.openOverflow(
          target,
          ids.filter(id =>
            quick.some(entry => entry.id === id && entry.overflows)
          )
        ),
    });
  }
  if (senior.length) {
    groups.push({
      id: 'senior',
      side: 'end',
      overflow: 'page',
      gap: 0,
      tools: senior.map(entry => tool(dock, entry)),
    });
  }
  return { locked, autoHide, island: true, groups };
}

/** A canvas's dock content, live. */
export function edgelessDockContent$(
  dock: EdgelessDock,
  presentationLabel: () => string
): Observable<EdgelessToolsContent> {
  return new Observable<EdgelessToolsContent>(subscriber =>
    dock.state$.subscribe(state =>
      subscriber.next(edgelessDockContent(dock, state, presentationLabel()))
    )
  );
}
